import { useEffect } from "react";
import { Switch } from "@headlessui/react";
import { canUsePremium, canUseUltraPremium } from "utils/planUtils";
import type { AsyncApiParams, CollectorConfig } from "providers/HostedScrapingProvider/types";
import { Tooltip, PureTooltip } from "components/Tooltip";
import { useSettingsFromApi } from "providers/ApiSettingsProvider";
import { ConfigProblem, ConfigProblemWithMessage } from "components/hostedScrapingValidators";
import { tooltips } from "./tooltips";
import { ProjectVariant } from "sdecontent";
import { disableFollowRedirectToFollowRedirect, followRedirectToDisableFollowRedirect } from "providers/HostedScrapingProvider/negateFollowRedirects";
import { cx } from "utils";
import { AdditionalOptionsTextBox } from "./AdditionalOptionsTextBox";
import { AdditionalOptionsListbox } from "./AdditionalOptionsListBox";
import { AdditionalOptionsNumberBox } from "./AdditionalOptionsNumberBox";
import { AdditionalOptionsMultiListbox, callbackWithCommaSeparatedString, splitCommaSeparatedString } from "./AdditionalOptionsMultiListBox";
import ParseResultsDropdown from "./ParseResultsDropdown";
import OutputFormatDropdown from "./OutputFormatDropdown";
import ApiPlaygroundComboBox from "routes/dashboard/components/ApiPlaygroundComboBox";
import { useFilteredApiParams } from "hooks/useFilteredApiParams";
import { useTLDs } from "hooks/useTLDs";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";


const availableDeviceTypes = [
  { text: 'None', value: undefined },
  { text: 'Desktop', value: 'desktop' },
  { text: 'Mobile', value: 'mobile' }
];

const countryCodes = (availableCountryCodes: string[]) => [
   { text: 'None', value: undefined },
  ...(availableCountryCodes.map((code) => ({ text: code, value: code })))
];

const restrictedCountryCodes = [
  { text: 'None', value: undefined },
  { text: 'us', value: 'us' },
  { text: 'eu', value: 'eu' },
];

type AdditionalOptionsSwitchProps = {
  primaryText: string,
  secondaryText?: string,
  checked: boolean,
  callback: (state: boolean) => void,
  testId?: string
};

const AdditionalOptionsSwitch = ({ primaryText, secondaryText, checked, callback, testId }: AdditionalOptionsSwitchProps) => {
  return (<div className="mt-5 flex flex-row flex-wrap gap-2">
    <Switch
      data-testid={testId}
      checked={checked}
      onChange={callback}
      className={cx(checked ? "bg-blue-600" : "bg-gray-200 dark:bg-neutral-200",
        "relative inline-flex h-6 w-11 items-center rounded-full z-0")}
    >
      <span
        className={cx(checked ? "translate-x-6" : "translate-x-1",
          "inline-block h-4 w-4 transform rounded-full bg-white transition")}
      />
    </Switch>
    <div>
      <span className="text-normal">
        {primaryText}
      </span>
      {secondaryText && <span className="text-sm ml-2 text-neutral-500 dark:text-neutral-500">{secondaryText}</span>}
    </div>
  </div>);
}

const urlProjectTextsHS = {
  premium: "Activate premium residential and mobile IPs",
  ultraPremium: "Activate advanced bypass mechanism",
  redirect: "Disable follow redirects",
  rendering: "Activate javascript rendering",
  screenshot: "Generate screenshot",
  autoparse: "Activate to parse results",
  follow404: "Activate retrying 404 responses",
  sessionNumber: "Session number",
  binaryTarget: "Binary target",
  keepHeaders: "Use own headers",
}

const urlLlmProjectTextsHS = {
  premium: "Activate premium residential and mobile IPs",
  ultraPremium: "Activate advanced bypass mechanism",
  redirect: "Disable follow redirects",
  rendering: "Activate javascript rendering",
  screenshot: "Generate screenshot",
  follow404: "Activate retrying 404 responses",
  sessionNumber: "Session number"
}

const urlProjectTextsAP = {
  premium: "Premium residential and mobile IPs",
  ultraPremium: "Advanced bypass mechanism",
  redirect: "Disable follow redirects",
  rendering: "Javascript rendering",
  screenshot: "Generate screenshot",
  autoparse: "Parse results",
  follow404: "Retry 404 responses",
  sessionNumber: "Session number",
  binaryTarget: "Binary target",
  keepHeaders: "Use own headers",
}

const hashasProblem = (problems: ConfigProblemWithMessage[]) => (problem: ConfigProblem) => problems.some(p => p.problem === problem);
const hasProblemMessage = (problems: ConfigProblemWithMessage[]) => (problem: ConfigProblem) => problems.find(p => p.problem === problem)?.message;

type UrlProjectAsyncApiParamsProps = {
  variant: ProjectVariant;
  collectorConfig: CollectorConfig;
  problems: ConfigProblemWithMessage[];
  updateApiParams: (key: string) => (newValue: string | boolean | number | undefined, apiParamsToChange?: AsyncApiParams) => AsyncApiParams;
};

export const UrlProjectAsyncApiParams = ({ variant, collectorConfig, problems, updateApiParams }: UrlProjectAsyncApiParamsProps) => {
  const { activeSubscription } = useActiveSubscription();
  const { availableCountryCodes } = useSettingsFromApi();
  // const config = collectorConfig as AsyncUrls;
  const countryCodesToShow = activeSubscription?.apiGeotargeting === "All Geotargeting" ? countryCodes(availableCountryCodes) : restrictedCountryCodes;

  const texts = variant === 'hosted-scraper' ? urlProjectTextsHS : urlProjectTextsAP;

  const hasProblem = hashasProblem(problems);
  const problemMessage = hasProblemMessage(problems);

  return (
    <>
      {canUsePremium(activeSubscription?.planSlug) &&
        <Tooltip content={tooltips.activatePremiumResidentialAndMobileIps} className="mt-5">
          <AdditionalOptionsSwitch primaryText={texts.premium} checked={Boolean(collectorConfig.apiParams?.premium)} callback={updateApiParams('premium')} testId="testPremiumSwitch" />
        </Tooltip>
      }

      {canUseUltraPremium(activeSubscription?.planSlug) &&
        <Tooltip content={tooltips.activateAdvancedBypassMechanism} className="mt-5">
          <AdditionalOptionsSwitch primaryText={texts.ultraPremium} checked={Boolean(collectorConfig.apiParams?.ultraPremium)} callback={updateApiParams('ultraPremium')} testId="testUltraPremiumSwitch" />
        </Tooltip>
      }

      <Tooltip content={tooltips.disableFollowRedirects} className="mt-5">
        <AdditionalOptionsSwitch primaryText={texts.redirect}
          checked={Boolean(followRedirectToDisableFollowRedirect(collectorConfig.apiParams?.followRedirect as boolean | undefined))}
          callback={(newValue) => updateApiParams('followRedirect')(disableFollowRedirectToFollowRedirect(newValue))}
          testId="testRedirectsSwitch" />
      </Tooltip>

      {/* <AdditionalOptionsSwitch primaryText="Keep headers" checked={Boolean(project.config?.apiParams?.keepHeaders)} callback={updateApiParams('keepHeaders')}/> */}

      <Tooltip content={tooltips.activateJavascriptRendering} className="mt-5">
        <AdditionalOptionsSwitch primaryText={texts.rendering} checked={Boolean(collectorConfig.apiParams?.render)} callback={updateApiParams('render')} testId="testRenderSwitch" />
      </Tooltip>


      <Tooltip content={tooltips.generateScreenshot} className="mt-5">
        <AdditionalOptionsSwitch primaryText={texts.screenshot} checked={Boolean(collectorConfig.apiParams?.screenshot)} callback={updateApiParams('screenshot')} testId="testScreenshotSwitch" />
      </Tooltip>

      {collectorConfig.apiParams?.render &&
        <>
          <div className="mt-5">
            <Tooltip content={tooltips.waitForSelector}>Wait for selector</Tooltip>
          </div>
          <AdditionalOptionsTextBox value={collectorConfig.apiParams?.waitForSelector as string | undefined} placeholder="Please add selector" callback={updateApiParams('waitForSelector')} />
        </>
      }

      {/* {
        (variant === 'hosted-scraper') &&
        (<Tooltip content={tooltips.autoparse} className="mt-5">
          <AdditionalOptionsSwitch primaryText={texts.autoparse} checked={Boolean(collectorConfig.apiParams?.autoparse)} callback={updateApiParams('autoparse')} />
        </Tooltip>)
      } */}
      <Tooltip content={tooltips.activateRetrying404Responses} className="mt-5">
        <AdditionalOptionsSwitch primaryText={texts.follow404} checked={Boolean(collectorConfig.apiParams?.retry404)} callback={updateApiParams('retry404')} />
      </Tooltip>

      <ParseResultsDropdown
        collectorConfig={collectorConfig}
        updateApiParams={updateApiParams}
        scrapingVariant={variant}
        mediumList={true}
      />

      <div className="mt-5">
        <Tooltip content={tooltips.countryCode}>Country code</Tooltip>
      </div>

      <AdditionalOptionsListbox value={collectorConfig.apiParams?.countryCode as string | undefined} options={countryCodesToShow} callback={updateApiParams('countryCode')} shortList buttonTestId="testCountryCode" />
      {hasProblem(ConfigProblem.InvalidCountryCode) && <div className="text-red-500 dark:text-error-500">Invalid country code</div>}

      <div className="mt-5">
        <Tooltip content={tooltips.deviceType}>Device type</Tooltip>
      </div>
      <AdditionalOptionsListbox value={collectorConfig.apiParams?.deviceType as string | undefined} options={availableDeviceTypes} callback={updateApiParams('deviceType')} buttonTestId="testDeviceType" />
      <div className="mt-5">
        <Tooltip content={tooltips.maxCost}>Max Cost</Tooltip>
      </div>
      <AdditionalOptionsNumberBox value={collectorConfig.apiParams?.maxCost as number | undefined} placeholder="Please set a max cost value" callback={(newValue: string | undefined) => {
        if (newValue === undefined) {
          updateApiParams('maxCost')(undefined);
          return;
        }
        const parsed = Number.parseInt(newValue, 10);
        updateApiParams('maxCost')(parsed);

      }} />
      {hasProblem(ConfigProblem.InvalidMaxCost) && <div className="text-red-500 dark:text-error-500">Invalid max cost number</div>}
      {hasProblem(ConfigProblem.MaxCostTooLow) && <div className="text-red-500 dark:text-error-500">{problemMessage(ConfigProblem.MaxCostTooLow)}</div>}


      {
        variant === 'api-playground' &&
        <>
          <div className="mt-5">
            <Tooltip content={tooltips.sessionNumber}>Session number</Tooltip>
          </div>
          <AdditionalOptionsNumberBox value={collectorConfig.apiParams?.sessionNumber as number | undefined} placeholder="Please set a session number" callback={(newValue: string | undefined) => {
            if (newValue === undefined) {
              updateApiParams('sessionNumber')(undefined);
              return;
            }
            const parsed = Number.parseInt(newValue, 10);
            updateApiParams('sessionNumber')(parsed);
          }} />
          {hasProblem(ConfigProblem.InvalidSessionNumber) && <div className="text-red-500 dark:text-error-500">Invalid session number</div>}

          <Tooltip content={tooltips.binaryTarget} className="mt-5">
            <AdditionalOptionsSwitch primaryText={texts.binaryTarget} checked={Boolean(collectorConfig.apiParams?.binaryTarget)} callback={updateApiParams('binaryTarget')} />
          </Tooltip>

          <Tooltip content={tooltips.keepHeaders} className="mt-5">
            <AdditionalOptionsSwitch primaryText={texts.keepHeaders} checked={Boolean(collectorConfig.apiParams?.keepHeaders)} callback={updateApiParams('keepHeaders')} />
          </Tooltip>
        </>
      }
    </>
  );
}

type UrlLlmProjectAsyncApiParamsProps = {
  variant?: ProjectVariant;
  collectorConfig: CollectorConfig;
  problems: ConfigProblemWithMessage[];
  updateApiParams: (key: string) => (newValue: string | boolean | number | undefined, apiParamsToChange?: AsyncApiParams) => AsyncApiParams;
};

export const UrlLlmProjectAsyncApiParams = ({ variant = 'hosted-scraper', collectorConfig, problems, updateApiParams }: UrlLlmProjectAsyncApiParamsProps) => {
  const { activeSubscription } = useActiveSubscription();
  const { availableCountryCodes } = useSettingsFromApi();
  const countryCodesToShow = activeSubscription?.apiGeotargeting === "All Geotargeting" ? countryCodes(availableCountryCodes) : restrictedCountryCodes;

  // only for hosted scraping
  const texts = urlLlmProjectTextsHS;

  const hasProblem = hashasProblem(problems);
  const problemMessage = hasProblemMessage(problems);

  return (
    <>
      {canUsePremium(activeSubscription?.planSlug) &&
        <Tooltip content={tooltips.activatePremiumResidentialAndMobileIps} className="mt-5">
          <AdditionalOptionsSwitch primaryText={texts.premium} checked={Boolean(collectorConfig.apiParams?.premium)} callback={updateApiParams('premium')} testId="testPremiumSwitch" />
        </Tooltip>
      }

      {canUseUltraPremium(activeSubscription?.planSlug) &&
        <Tooltip content={tooltips.activateAdvancedBypassMechanism} className="mt-5">
          <AdditionalOptionsSwitch primaryText={texts.ultraPremium} checked={Boolean(collectorConfig.apiParams?.ultraPremium)} callback={updateApiParams('ultraPremium')} testId="testUltraPremiumSwitch" />
        </Tooltip>
      }

      <Tooltip content={tooltips.disableFollowRedirects} className="mt-5">
        <AdditionalOptionsSwitch primaryText={texts.redirect}
          checked={Boolean(followRedirectToDisableFollowRedirect(collectorConfig.apiParams?.followRedirect as boolean | undefined))}
          callback={(newValue) => updateApiParams('followRedirect')(disableFollowRedirectToFollowRedirect(newValue))}
          testId="testRedirectsSwitch" />
      </Tooltip>

      <Tooltip content={tooltips.activateJavascriptRendering} className="mt-5">
        <AdditionalOptionsSwitch primaryText={texts.rendering} checked={Boolean(collectorConfig.apiParams?.render)} callback={updateApiParams('render')} testId="testRenderSwitch" />
      </Tooltip>

      <Tooltip content={tooltips.generateScreenshot} className="mt-5">
        <AdditionalOptionsSwitch primaryText={texts.screenshot} checked={Boolean(collectorConfig.apiParams?.screenshot)} callback={updateApiParams('screenshot')} testId="testScreenshotSwitch" />
      </Tooltip>

      {collectorConfig.apiParams?.render &&
        <>
          <div className="mt-5">
            <Tooltip content={tooltips.waitForSelector}>Wait for selector</Tooltip>
          </div>
          <AdditionalOptionsTextBox value={collectorConfig.apiParams?.waitForSelector as string | undefined} placeholder="Please add selector" callback={updateApiParams('waitForSelector')} />
        </>
      }

      <Tooltip content={tooltips.activateRetrying404Responses} className="mt-5">
        <AdditionalOptionsSwitch primaryText={texts.follow404} checked={Boolean(collectorConfig.apiParams?.retry404)} callback={updateApiParams('retry404')} />
      </Tooltip>

      <ParseResultsDropdown
        collectorConfig={collectorConfig}
        updateApiParams={updateApiParams}
        scrapingVariant={variant}
      />

      <div className="mt-5">
        <Tooltip content={tooltips.countryCode}>Country code</Tooltip>
      </div>

      <AdditionalOptionsListbox value={collectorConfig.apiParams?.countryCode as string | undefined} options={countryCodesToShow} callback={updateApiParams('countryCode')} shortList buttonTestId="testCountryCode" />
      {hasProblem(ConfigProblem.InvalidCountryCode) && <div className="text-red-500 dark:text-error-500">Invalid country code</div>}

      <div className="mt-5">
        <Tooltip content={tooltips.deviceType}>Device type</Tooltip>
      </div>
      <AdditionalOptionsListbox value={collectorConfig.apiParams?.deviceType as string | undefined} options={availableDeviceTypes} callback={updateApiParams('deviceType')} buttonTestId="testDeviceType" />
      <div className="mt-5">
        <Tooltip content={tooltips.maxCost}>Max Cost</Tooltip>
      </div>
      <AdditionalOptionsNumberBox value={collectorConfig.apiParams?.maxCost as number | undefined} placeholder="Please set a max cost value" callback={(newValue: string | undefined) => {
        if (newValue === undefined) {
          updateApiParams('maxCost')(undefined);
          return;
        }
        const parsed = Number.parseInt(newValue, 10);
        updateApiParams('maxCost')(parsed);

      }} />
      {hasProblem(ConfigProblem.InvalidMaxCost) && <div className="text-red-500 dark:text-error-500">Invalid max cost number</div>}
      {hasProblem(ConfigProblem.MaxCostTooLow) && <div className="text-red-500 dark:text-error-500">{problemMessage(ConfigProblem.MaxCostTooLow)}</div>}
    </>
  );
}

type AmazonProjectAsyncApiParamsProps = {
  collectorConfig: CollectorConfig,
  problems: ConfigProblemWithMessage[],
  updateApiParams: (key: string) => (newValue: string | boolean | number | undefined) => AsyncApiParams,
  checkSubscription: boolean
  withOutputFormat?: boolean
}

export const AmazonProjectAsyncApiParams = ({ collectorConfig, problems, updateApiParams, checkSubscription, withOutputFormat = false }: AmazonProjectAsyncApiParamsProps) => {
  const { activeSubscription } = useActiveSubscription();
  const { availableCountryCodes } = useSettingsFromApi();
  const { tlds: validAmazonTLDs } = useTLDs('amazon');
  const countryCodesToShow = (!checkSubscription || (activeSubscription?.apiGeotargeting === "All Geotargeting")) ? countryCodes(availableCountryCodes) : restrictedCountryCodes;

  const domainsToShow = [
    { text: 'None', value: undefined },
    ...validAmazonTLDs.map((tld: string) => ({ text: tld, value: tld }))
  ];

  const amazonOfferParams = [
    { text: 'New', value: 'F_New' },
    { text: 'Used Like New', value: 'F_UsedLikeNew' },
    { text: 'Used Very Good', value: 'F_UsedVeryGood' },
    { text: 'Used Good', value: 'F_UsedGood' },
    { text: 'Used Acceptable', value: 'F_UsedAcceptable' },
  ]

  const hasProblem = hashasProblem(problems);

  // update on load
  useFilteredApiParams(collectorConfig, countryCodesToShow, domainsToShow);

  const texts = urlProjectTextsHS;

  return (
    <>
      <div className="mt-5">
        <Tooltip content={tooltips.countryCode}>Country code</Tooltip>
      </div>

      <ApiPlaygroundComboBox
        value={collectorConfig?.apiParams?.countryCode}
        data={countryCodesToShow}
        onChange={updateApiParams('countryCode')}
        type='apiParams'
        restoreOnBlurOrEscape={false}
        placeholder='none'
        testId="sde-countrycode-combobox"
      />

      {hasProblem(ConfigProblem.InvalidCountryCode) && <div className="text-red-500 dark:text-error-500">Invalid country code</div>}

      <div className="mt-5">
        <Tooltip content={tooltips.topLevelDomain}>Domain</Tooltip>
      </div>

      <ApiPlaygroundComboBox
        value={collectorConfig?.apiParams?.tld as string | undefined}
        data={domainsToShow}
        onChange={updateApiParams('tld')}
        type='apiParams'
        placeholder='none'
        testId="sde-tld-combobox"
      />

      {hasProblem(ConfigProblem.InvalidTLD) && <div className="text-red-500 dark:text-error-500">Invalid TLD</div>}

      {withOutputFormat && <OutputFormatDropdown collectorConfig={collectorConfig} updateApiParams={updateApiParams} />}

      {
        (collectorConfig.type === 'async_amazon_offer') &&
        <>
          <div className="mt-5">
            <Tooltip content={tooltips.amazonOfferParams}>Condition</Tooltip>
          </div>
          <AdditionalOptionsMultiListbox values={splitCommaSeparatedString(collectorConfig?.apiParams?.condition as string | undefined)} options={amazonOfferParams} callback={callbackWithCommaSeparatedString(updateApiParams('condition'))} shortList />
        </>
      }


      {
        (collectorConfig.type === 'async_amazon_search') &&
        <>
          <div className="mt-5">
            <Tooltip content={tooltips.amazonPage}>Page</Tooltip>
          </div>
          <AdditionalOptionsTextBox value={collectorConfig?.apiParams?.page as string | undefined} placeholder="Please enter page number" callback={updateApiParams('page')} />
          {hasProblem(ConfigProblem.InvalidNum) && <div className="text-red-500 dark:text-error-500">Invalid page number</div>}
        </>
      }

      <>
        <Tooltip content={tooltips.activateRetrying404Responses} className="mt-5">
          <AdditionalOptionsSwitch primaryText={texts.follow404} checked={Boolean(collectorConfig.apiParams?.retry404)} callback={updateApiParams('retry404')} />
        </Tooltip>
      </>


      {/* <div className="mt-5">
        Select format
      </div>
      <AdditionalOptionsListbox
        value={config?.apiParams?.outputFormat === 'csv' ? 'csv' : 'json'}
        options={[
          { value: 'csv', text: 'CSV' },
          { value: 'json', text: 'JSON' }
        ]}
        callback={updateApiParams('outputFormat')}
        /> */}
    </>
  );
}

type GoogleProjectAsyncApiParamsProps = {
  collectorConfig: CollectorConfig,
  problems: ConfigProblemWithMessage[],
  updateApiParams: (key: string) => (newValue: number | string | boolean | undefined) => AsyncApiParams,
  checkSubscription: boolean,
  withOutputFormat?: boolean
}
export const GoogleProjectAsyncApiParams = ({ collectorConfig, problems, updateApiParams, checkSubscription, withOutputFormat = false }: GoogleProjectAsyncApiParamsProps) => {
  const { activeSubscription } = useActiveSubscription();
  const { availableCountryCodes } = useSettingsFromApi();
  const { tlds: validGoogleTLDs } = useTLDs('google');
  const countryCodesToShow = (!checkSubscription || (activeSubscription?.apiGeotargeting === "All Geotargeting")) ? countryCodes(availableCountryCodes) : restrictedCountryCodes;

  const hasProblem = hashasProblem(problems);

  const domainsToShow = [
    { text: 'None', value: undefined },
    ...validGoogleTLDs.map((tld: string) => ({ text: tld, value: tld }))
  ];

  const timeBasedSearchOptions = [
    { text: 'None', value: undefined },
    { text: 'Past hour', value: 'h' },
    { text: 'Past 24 hours', value: 'd' },
    { text: 'Past week', value: 'w' },
    { text: 'Past month', value: 'm' },
    { text: 'Past year', value: 'y' }
  ];

  // update on load
  useFilteredApiParams(collectorConfig, countryCodesToShow, domainsToShow);

  return (
    <>
      {collectorConfig.type === 'async_google_maps_search' && (
        <>
          <div className="mt-5">
            <Tooltip content={tooltips.googleLatitude}>Latitude</Tooltip>
            <AdditionalOptionsTextBox type="number" value={collectorConfig?.apiParams?.latitude as number | undefined} placeholder="Please enter a Latitude coordinate" callback={updateApiParams('latitude')} />
            {hasProblem(ConfigProblem.InvalidLatitude) && <div className="text-red-500 dark:text-error-500">Invalid Latitude</div>}
          </div>

          <div className="mt-5">
            <Tooltip content={tooltips.googleLongitude}>Longitude</Tooltip>
            <AdditionalOptionsTextBox type="number" value={collectorConfig?.apiParams?.longitude as number | undefined} placeholder="Please enter a Longitude coordinate" callback={updateApiParams('longitude')} />
            {hasProblem(ConfigProblem.InvalidLongitude) && <div className="text-red-500 dark:text-error-500">Invalid Longitude</div>}
          </div>
        </>
      )}

      <div className="mt-5">
        <Tooltip content={tooltips.countryCode}>Country code</Tooltip>
      </div>

      <ApiPlaygroundComboBox
        value={collectorConfig?.apiParams?.countryCode}
        data={countryCodesToShow}
        onChange={updateApiParams('countryCode')}
        type='apiParams'
        restoreOnBlurOrEscape={false}
        placeholder='none'
        testId="sde-countrycode-combobox"
      />
      {hasProblem(ConfigProblem.InvalidCountryCode) && <div className="text-red-500 dark:text-error-500">Invalid country code</div>}

      <div className="mt-5">
        <Tooltip content={tooltips.topLevelDomain}>Domain</Tooltip>
      </div>

      <ApiPlaygroundComboBox
        value={collectorConfig?.apiParams?.tld as string | undefined}
        data={domainsToShow}
        onChange={updateApiParams('tld')}
        type='apiParams'
        placeholder='none'
        testId="sde-tld-combobox"
      />
      {hasProblem(ConfigProblem.InvalidTLD) && <div className="text-red-500 dark:text-error-500">Invalid TLD</div>}

      {(collectorConfig.type === 'async_google_search' || collectorConfig.type === 'async_google_news') && (
      <>
        <div className="mt-5">
          <Tooltip content={tooltips.TBS}>Time based search</Tooltip>
        </div>

        <ApiPlaygroundComboBox
          value={collectorConfig?.apiParams?.tbs as string | undefined}
          data={timeBasedSearchOptions}
          onChange={updateApiParams('tbs')}
          type='apiParams'
          placeholder='none'
          testId="sde-tbs-combobox"
        />
      </>
      )}

      {withOutputFormat && <OutputFormatDropdown collectorConfig={collectorConfig} updateApiParams={updateApiParams} />}

      <div className="mt-5">
        <Tooltip content={tooltips.UULE}>UULE</Tooltip>
      </div>
      <AdditionalOptionsTextBox value={collectorConfig?.apiParams?.uule as string | undefined} placeholder="Please enter a UULE" callback={updateApiParams('uule')} />
      {hasProblem(ConfigProblem.InvalidUULE) && <div className="text-red-500 dark:text-error-500">Invalid UULE</div>}

      {
        collectorConfig.type === 'async_google_search' &&
        <>
          <div className="mt-5">
            <Tooltip content={tooltips.additionalParams}>Additional parameters</Tooltip>
          </div>
          <textarea className="w-full border-gray-200 dark:border-neutral-200 placeholder-gray-200 dark:placeholder-neutral-200 text-sm" value={collectorConfig?.apiParams?.additionalParams as string} placeholder='Enter additional parameters and separate with "&" (ampersand) sign.' onChange={(ev) => updateApiParams('additionalParams')(ev.target.value)} />
          {hasProblem(ConfigProblem.InvalidAdditionalParameters) && <div className="text-red-500 dark:text-error-500">Invalid parameters</div>}
        </>
      }
    </>
  );
}


type WalmartProjectAsyncApiParamsProps = {
  collectorConfig: CollectorConfig,
  problems: ConfigProblemWithMessage[],
  updateApiParams: (key: string) => (newValue: string | boolean | number | undefined) => AsyncApiParams,
  withOutputFormat?: boolean
}

export const WalmartProjectAsyncApiParams = ({ collectorConfig, problems, updateApiParams, withOutputFormat = false }: WalmartProjectAsyncApiParamsProps) => {
  const { tlds: validWalmartTLDs } = useTLDs('walmart');

  const hasProblem = hashasProblem(problems);

  const domainsToShow = [
    { text: 'None', value: undefined },
    ...validWalmartTLDs.map((tld: string) => ({ text: tld, value: tld }))
  ];

  const validSortByOptions = [
    { text: 'None', value: undefined },
    { text: 'Relevancy', value: 'relevancy' },
    { text: 'Helpfulness', value: 'helpful' },
    { text: 'Submission - descending', value: 'submission-desc' },
    { text: 'Submission - ascending', value: 'submission-asc' },
    { text: 'Rating - descending', value: 'rating-desc' },
    { text: 'Rating - ascending', value: 'rating-asc' },
  ];

  // update on load
  useFilteredApiParams(collectorConfig, undefined, domainsToShow);

  return (
    <>
      <div className="mt-5">
        <Tooltip content={tooltips.topLevelDomain}>Domain</Tooltip>
      </div>
      <AdditionalOptionsListbox value={collectorConfig?.apiParams?.tld as string | undefined ?? ""} options={domainsToShow} callback={updateApiParams('tld')} shortList />
      {hasProblem(ConfigProblem.InvalidTLD) && <div className="text-red-500 dark:text-error-500">Invalid TLD</div>}

      {
        (collectorConfig.type === 'async_walmart_search' || collectorConfig.type === 'async_walmart_category' || collectorConfig.type === 'async_walmart_review') &&
        <>
          <div className="mt-5">
            <Tooltip content={tooltips.walmartPage}>Page</Tooltip>
          </div>
          <AdditionalOptionsTextBox value={collectorConfig?.apiParams?.page as string | undefined} placeholder="Please enter page number" callback={updateApiParams('page')} />
          {hasProblem(ConfigProblem.InvalidNum) && <div className="text-red-500 dark:text-error-500">Invalid page number</div>}
        </>
      }
      {
        (collectorConfig.type === 'async_walmart_review') &&
        <>
          <div className="mt-5">
            <Tooltip content={tooltips.walmartSortBy}>Sort by</Tooltip>
          </div>
          <AdditionalOptionsListbox value={collectorConfig?.apiParams?.sort as string | undefined} options={validSortByOptions} callback={updateApiParams('sort')} shortList />
          {hasProblem(ConfigProblem.InvalidSortBy) && <div className="text-red-500 dark:text-error-500">Invalid sort by</div>}
        </>
      }

      {withOutputFormat && <OutputFormatDropdown collectorConfig={collectorConfig} updateApiParams={updateApiParams} />}
    </>
  );
}

type HomeDepotProjectAsyncApiParamsProps = {
  variant: ProjectVariant,
  collectorConfig: CollectorConfig,
  problems: ConfigProblemWithMessage[],
  updateApiParams: (key: string) => (newValue: string | boolean | number | undefined) => AsyncApiParams,
  withOutputFormat?: boolean
}

export const HomeDepotProjectAsyncApiParams = ({ variant, collectorConfig, problems, updateApiParams, withOutputFormat = false }: HomeDepotProjectAsyncApiParamsProps) => {
  const hasProblem = hashasProblem(problems);

  const validSortByOptions = [
    { text: 'None', value: undefined },
    { text: 'Best seller', value: 'best_seller' },
    { text: 'Most popular', value: 'most_popular' },
    { text: 'Price low to high', value: 'price_low_to_high' },
    { text: 'Price high to low', value: 'price_high_to_low' },
    { text: 'Highest rating', value: 'highest_rating' },
  ];

  return (
    <>
      {/* Playground only: url is a single-request alternative to search_term, which batch projects map one-per-row. */}
      {variant === 'api-playground' && <>
        <div className="mt-5">Search results URL</div>
        <div className="text-sm text-gray-500 dark:text-gray-400">Optional. Overrides the search term and other filters.</div>
        <AdditionalOptionsTextBox value={collectorConfig?.apiParams?.url as string | undefined} placeholder="https://www.homedepot.com/s/lawn+mower" callback={updateApiParams('url')} />
        {hasProblem(ConfigProblem.InvalidHomeDepotUrl) && <div className="text-red-500 dark:text-error-500">Enter a Home Depot search (/s/) or browse (/b/) results URL</div>}
      </>}

      <div className="mt-5">Sort by</div>
      <AdditionalOptionsListbox value={collectorConfig?.apiParams?.sortBy as string | undefined} options={validSortByOptions} callback={updateApiParams('sortBy')} shortList />
      {hasProblem(ConfigProblem.InvalidSortBy) && <div className="text-red-500 dark:text-error-500">Invalid sort by</div>}

      <div className="mt-5">Minimum price</div>
      <AdditionalOptionsTextBox value={collectorConfig?.apiParams?.minPrice as string | undefined} placeholder="Please enter a minimum price" callback={updateApiParams('minPrice')} />

      <div className="mt-5">Maximum price</div>
      <AdditionalOptionsTextBox value={collectorConfig?.apiParams?.maxPrice as string | undefined} placeholder="Please enter a maximum price" callback={updateApiParams('maxPrice')} />
      {hasProblem(ConfigProblem.InvalidPrice) && <div className="text-red-500 dark:text-error-500">Invalid price</div>}

      <div className="mt-5">Page</div>
      <AdditionalOptionsTextBox value={collectorConfig?.apiParams?.page as string | undefined} placeholder="Please enter page number" callback={updateApiParams('page')} />
      {hasProblem(ConfigProblem.InvalidNum) && <div className="text-red-500 dark:text-error-500">Invalid page number</div>}

      {withOutputFormat && <OutputFormatDropdown collectorConfig={collectorConfig} updateApiParams={updateApiParams} />}
    </>
  );
}

type HomeDepotProductProjectAsyncApiParamsProps = {
  variant: ProjectVariant,
  collectorConfig: CollectorConfig,
  problems: ConfigProblemWithMessage[],
  updateApiParams: (key: string) => (newValue: string | boolean | number | undefined) => AsyncApiParams,
  withOutputFormat?: boolean
}

export const HomeDepotProductProjectAsyncApiParams = ({ variant, collectorConfig, problems, updateApiParams, withOutputFormat = false }: HomeDepotProductProjectAsyncApiParamsProps) => {
  const hasProblem = hashasProblem(problems);

  return (
    <>
      {/* Playground only: url/gtin are single-request alternatives to item_id, which batch projects map one-per-row. */}
      {variant === 'api-playground' && <>
        <div className="mt-5">Product URL</div>
        <div className="text-sm text-gray-500 dark:text-gray-400">Optional. Alternative to the item ID.</div>
        <AdditionalOptionsTextBox value={collectorConfig?.apiParams?.url as string | undefined} placeholder="https://www.homedepot.com/p/.../320326855" callback={updateApiParams('url')} />
        {hasProblem(ConfigProblem.InvalidHomeDepotProductUrl) && <div className="text-red-500 dark:text-error-500">Enter a Home Depot product (/p/) URL</div>}

        <div className="mt-5">GTIN / UPC</div>
        <div className="text-sm text-gray-500 dark:text-gray-400">Optional. Alternative to the item ID.</div>
        <AdditionalOptionsTextBox value={collectorConfig?.apiParams?.gtin as string | undefined} placeholder="045242637720" callback={updateApiParams('gtin')} />
        {hasProblem(ConfigProblem.InvalidGtin) && <div className="text-red-500 dark:text-error-500">Enter a numeric GTIN/UPC (8&ndash;14 digits)</div>}
      </>}

      {withOutputFormat && <OutputFormatDropdown collectorConfig={collectorConfig} updateApiParams={updateApiParams} />}
    </>
  );
}

type HomeDepotReviewsProjectAsyncApiParamsProps = {
  variant: ProjectVariant,
  collectorConfig: CollectorConfig,
  problems: ConfigProblemWithMessage[],
  updateApiParams: (key: string) => (newValue: string | boolean | number | undefined) => AsyncApiParams,
  withOutputFormat?: boolean
}

export const HomeDepotReviewsProjectAsyncApiParams = ({ variant, collectorConfig, problems, updateApiParams, withOutputFormat = false }: HomeDepotReviewsProjectAsyncApiParamsProps) => {
  const hasProblem = hashasProblem(problems);

  return (
    <>
      {/* Playground only: url/gtin are single-request alternatives to item_id, which batch projects map one-per-row. */}
      {variant === 'api-playground' && <>
        <div className="mt-5">Product URL</div>
        <div className="text-sm text-gray-500 dark:text-gray-400">Optional. Alternative to the item ID.</div>
        <AdditionalOptionsTextBox value={collectorConfig?.apiParams?.url as string | undefined} placeholder="https://www.homedepot.com/p/.../320326855" callback={updateApiParams('url')} />
        {hasProblem(ConfigProblem.InvalidHomeDepotProductUrl) && <div className="text-red-500 dark:text-error-500">Enter a Home Depot product (/p/) URL</div>}

        <div className="mt-5">GTIN / UPC</div>
        <div className="text-sm text-gray-500 dark:text-gray-400">Optional. Alternative to the item ID.</div>
        <AdditionalOptionsTextBox value={collectorConfig?.apiParams?.gtin as string | undefined} placeholder="045242637720" callback={updateApiParams('gtin')} />
        {hasProblem(ConfigProblem.InvalidGtin) && <div className="text-red-500 dark:text-error-500">Enter a numeric GTIN/UPC (8&ndash;14 digits)</div>}
      </>}

      <div className="mt-5">Page</div>
      <AdditionalOptionsTextBox value={collectorConfig?.apiParams?.page as string | undefined} placeholder="Please enter page number" callback={updateApiParams('page')} />
      {hasProblem(ConfigProblem.InvalidNum) && <div className="text-red-500 dark:text-error-500">Invalid page number</div>}

      {withOutputFormat && <OutputFormatDropdown collectorConfig={collectorConfig} updateApiParams={updateApiParams} />}
    </>
  );
}

type EbayProjectAsyncApiParamsProps = {
  collectorConfig: CollectorConfig,
  problems: ConfigProblemWithMessage[],
  updateApiParams: (key: string) => (newValue: string | boolean | number | undefined) => AsyncApiParams,
  withOutputFormat?: boolean
}

export const EbayProjectAsyncApiParams = ({ collectorConfig, problems, updateApiParams, withOutputFormat = false }: EbayProjectAsyncApiParamsProps) => {
  const { activeSubscription } = useActiveSubscription();
  const { availableCountryCodes } = useSettingsFromApi();
  const { tlds: validEbayTLDs } = useTLDs('ebay');
  const countryCodesToShow = activeSubscription?.apiGeotargeting === "All Geotargeting" ? countryCodes(availableCountryCodes) : restrictedCountryCodes;

  const hasProblem = hashasProblem(problems);

  const domainsToShow = [
    { text: 'None', value: undefined },
    ...validEbayTLDs.map((tld: string) => ({ text: tld, value: tld }))
  ];

  const conditionsToShow = [
    { text: 'None', value: undefined },
    { text: 'New', value: 'new' },
    { text: 'Used', value: 'used' },
    { text: 'Open box', value: 'open_box' },
    { text: 'Refurbished', value: 'refurbished' },
    { text: 'For parts', value: 'for_parts' },
    { text: 'Not working', value: 'not_working' }
  ];

  const validSortByOptions = [
    { text: 'None', value: undefined },
    { text: 'Ending soonest', value: 'ending_soonest' },
    { text: 'Newly listed', value: 'newly_listed' },
    { text: 'Price lowest', value: 'price_lowest' },
    { text: 'Price highest', value: 'price_highest' },
    { text: 'Distance nearest', value: 'distance_nearest' },
    { text: 'Best match', value: 'best_match' }
  ];

  const validShowOnlyOptions = [
    { text: 'Returns accepted', value: 'returns_accepted' },
    { text: 'Authorized seller', value: 'authorized_seller' },
    { text: 'Completed items', value: 'completed_items' },
    { text: 'Sold items', value: 'sold_items' },
    { text: 'Sale items', value: 'sale_items' },
    // { text:'Deals and savings', value: 'deals_and_savings' },
    { text: 'Listed as lots', value: 'listed_as_lots' },
    { text: 'Search in description', value: 'search_in_description' },
    { text: 'Benefits charity', value: 'benefits_charity' },
    { text: 'Authenticity guarantee', value: 'authenticity_guarantee' },
    // { text:'PSA vault', value: 'psa_vault' }
  ];

  const validBuyingFormatOptions = [
    { text: 'None', value: undefined },
    { text: 'Buy it now', value: 'buy_it_now' },
    { text: 'Auction', value: 'auction' },
    { text: 'Accepts offers', value: 'accepts_offers' },
  ];

  // update on load
  useFilteredApiParams(collectorConfig, countryCodesToShow, domainsToShow);

  return (
    <>
      <div className="mt-5">
        <Tooltip content={tooltips.topLevelDomain}>Domain</Tooltip>
      </div>
      <ApiPlaygroundComboBox
        value={collectorConfig?.apiParams?.tld as string | undefined}
        data={domainsToShow}
        onChange={updateApiParams('tld')}
        type='apiParams'
        placeholder='none'
        testId="sde-tld-combobox"
      />
      {hasProblem(ConfigProblem.InvalidTLD) && <div className="text-red-500">Invalid TLD</div>}

      <div className="mt-5">
        <Tooltip content={tooltips.countryCode}>Country code</Tooltip>
      </div>
      <AdditionalOptionsListbox value={collectorConfig.apiParams?.countryCode as string | undefined} options={countryCodesToShow} callback={updateApiParams('countryCode')} shortList buttonTestId="testCountryCode" />
      {hasProblem(ConfigProblem.InvalidCountryCode) && <div className="text-red-500">Invalid country code</div>}

      {(collectorConfig.type === 'async_ebay_search' &&
        <>
          <div className="mt-5">
            <Tooltip content={tooltips.ebaySearchPage}>Page</Tooltip>
          </div>
          <AdditionalOptionsNumberBox value={collectorConfig?.apiParams?.page as string | undefined} callback={updateApiParams('page')} placeholder="1" />
          {hasProblem(ConfigProblem.InvalidNum) && <div className="text-red-500">Invalid page number</div>}

          <div className="mt-5">
            <Tooltip content={tooltips.ebaySearchItemsPerPage}>Items per page</Tooltip>
          </div>
          <AdditionalOptionsNumberBox value={collectorConfig?.apiParams?.items_per_page as string | undefined} callback={updateApiParams('items_per_page')} placeholder="20" />
          {hasProblem(ConfigProblem.InvalidItemsPerPage) && <div className="text-red-500">Invalid items per page value</div>}

          <div className="mt-5">
            <Tooltip content={tooltips.ebaySearchSellerId}>Seller id</Tooltip>
          </div>
          <AdditionalOptionsTextBox value={collectorConfig?.apiParams?.seller_id as string | undefined} callback={updateApiParams('seller_id')} placeholder="seller_name" />
          {/* {hasProblem(ConfigProblem.InvalidSellerId) && <div className="text-red-500">Invalid seller id</div>} */}

          <div className="mt-5">
            <Tooltip content={tooltips.ebaySearchCondition}>Condition</Tooltip>
          </div>
          <AdditionalOptionsMultiListbox values={splitCommaSeparatedString(collectorConfig?.apiParams?.condition as string | undefined)} options={conditionsToShow} callback={callbackWithCommaSeparatedString(updateApiParams('condition'))} shortList />
          {/* {hasProblem(ConfigProblem.InvalidCondition) && <div className="text-red-500">Invalid condition</div>} */}

          <div className="mt-5">
            <Tooltip content={tooltips.ebayShowOnly}>Show only</Tooltip>
          </div>
          <AdditionalOptionsMultiListbox values={splitCommaSeparatedString(collectorConfig?.apiParams?.show_only as string | undefined)} options={validShowOnlyOptions} callback={callbackWithCommaSeparatedString(updateApiParams('show_only'))} shortList />
          {/* {hasProblem(ConfigProblem.InvalidCondition) && <div className="text-red-500">Invalid condition</div>} */}

          <div className="mt-5">
            <Tooltip content={tooltips.ebayBuyingFormat}>Buying format</Tooltip>
          </div>
          <AdditionalOptionsListbox value={collectorConfig?.apiParams?.buying_format as string | undefined} options={validBuyingFormatOptions} callback={updateApiParams('buying_format')} shortList />
          {/* {hasProblem(ConfigProblem.InvalidCondition) && <div className="text-red-500">Invalid condition</div>} */}

          <div className="mt-5">
            <Tooltip content={tooltips.ebaySearchSortBy}>Sort by</Tooltip>
          </div>
          <AdditionalOptionsListbox value={collectorConfig?.apiParams?.sort_by as string | undefined} options={validSortByOptions} callback={updateApiParams('sort_by')} shortList />
          {hasProblem(ConfigProblem.InvalidSortBy) && <div className="text-red-500 dark:text-error-500">Invalid sort by</div>}
        </>
      )}

      { withOutputFormat && <OutputFormatDropdown collectorConfig={collectorConfig} updateApiParams={updateApiParams} />}
    </>
  );
}

type RedfinProjectAsyncApiParamsProps = {
  collectorConfig: CollectorConfig,
  problems: ConfigProblemWithMessage[],
  updateApiParams: (key: string) => (newValue: string | boolean | number | undefined, apiParamsToChange?: AsyncApiParams) => AsyncApiParams,
  checkSubscription: boolean,
  withOutputFormat?: boolean
}

export const RedfinProjectAsyncApiParams = ({ collectorConfig, problems, updateApiParams, checkSubscription, withOutputFormat = false }: RedfinProjectAsyncApiParamsProps) => {
  const { activeSubscription } = useActiveSubscription();
  const { availableCountryCodes } = useSettingsFromApi();
  const { tlds: validRedfinTLDs } = useTLDs('redfin');
  const countryCodesToShow = (!checkSubscription || (activeSubscription?.apiGeotargeting === "All Geotargeting")) ? countryCodes(availableCountryCodes) : restrictedCountryCodes;

  const hasProblem = hashasProblem(problems);

  const domainsToShow = [
    { text: 'None', value: undefined },
    ...validRedfinTLDs.map((tld: string) => ({ text: tld, value: tld }))
  ];

  // update on load
  useFilteredApiParams(collectorConfig, countryCodesToShow, domainsToShow);

  return (
    <>
      <div className="mt-5">
        <Tooltip content={tooltips.countryCode}>Country code</Tooltip>
      </div>
      <ApiPlaygroundComboBox
        value={collectorConfig?.apiParams?.countryCode}
        data={countryCodesToShow}
        onChange={updateApiParams('countryCode')}
        type='apiParams'
        restoreOnBlurOrEscape={false}
        placeholder='none'
        testId="sde-countrycode-combobox"
      />
      {hasProblem(ConfigProblem.InvalidCountryCode) && <div className="text-red-500 dark:text-error-500">Invalid country code</div>}

      <div className="mt-5">
        <Tooltip content={tooltips.topLevelDomain}>Domain</Tooltip>
      </div>

      <AdditionalOptionsListbox value={collectorConfig?.apiParams?.tld as string | undefined} options={domainsToShow} callback={updateApiParams('tld')} shortList />
      {hasProblem(ConfigProblem.InvalidTLD) && <div className="text-red-500 dark:text-error-500">Invalid TLD</div>}

      <Tooltip content={tooltips.rawContent} className="mt-5">
        <AdditionalOptionsSwitch primaryText="Raw JSON by Redfin" secondaryText="Please note that the format of this JSON is not guaranteed." checked={Boolean(collectorConfig.apiParams?.raw)} callback={updateApiParams('raw')} testId="testRawSwitch" />
      </Tooltip>

      {withOutputFormat && <OutputFormatDropdown collectorConfig={collectorConfig} updateApiParams={updateApiParams} />}
    </>
  );
}

type AiProjectAsyncApiParamsProps = {
  collectorConfig: CollectorConfig,
  updateApiParams: (key: string) => (newValue: string | boolean | number | undefined, apiParamsToChange?: AsyncApiParams) => AsyncApiParams,
  withOutputFormat?: boolean
}

export const AiProjectAsyncApiParams = ({ collectorConfig, updateApiParams, withOutputFormat = false }: AiProjectAsyncApiParamsProps) => {
  return (
    <>
      {withOutputFormat && <OutputFormatDropdown collectorConfig={collectorConfig} updateApiParams={updateApiParams} />}
    </>
  );
}

type ChatGptSourcesToggleProps = {
  checked: boolean,
  onChange: (checked: boolean) => void,
}

// Free-plan users see it disabled with an upgrade hint.
export const ChatGptSourcesToggle = ({ checked, onChange }: ChatGptSourcesToggleProps) => {
  const { activeSubscription } = useActiveSubscription();
  const disabled = !canUsePremium(activeSubscription?.planSlug);

  useEffect(() => {
    if (disabled && checked) onChange(false);
  }, [ disabled, checked, onChange ]);

  const control = (
    <label className={cx("mt-3 flex w-fit items-center gap-3", disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer")}>
      <Switch
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className={cx(checked ? "bg-blue-600" : "bg-gray-200", "relative inline-flex h-6 w-11 items-center rounded-full")}
      >
        <span className={cx(checked ? "translate-x-6" : "translate-x-1", "inline-block h-4 w-4 transform rounded-full bg-white transition")} />
      </Switch>
      <span className="font-semibold">Include sources</span>
    </label>
  );

  return (
    <>
      {disabled
        ? <PureTooltip content="To use this toggle you need to be on a paid plan." className="w-fit">{control}</PureTooltip>
        : control}
      <div className="mt-1 text-sm text-neutral-500">Sources are unique to ChatGPT - toggle on to return cited links with the answer.</div>
    </>
  );
};

type IncludeHtmlToggleProps = {
  checked: boolean,
  onChange: (checked: boolean) => void,
}

export const IncludeHtmlToggle = ({ checked, onChange }: IncludeHtmlToggleProps) => (
  <div className="flex items-center">
  <Tooltip content="Return the raw HTML of the result alongside the structured answer." className="h-fit mt-5">
    <AdditionalOptionsSwitch
      primaryText="Include HTML"
      checked={checked}
      callback={onChange}
      testId="testIncludeHtml"
    />
  </Tooltip>
  </div>
);
